import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { LaborRate, CreateLaborRate, UpdateLaborRate, LaborRateTestInput } from '@estimator/shared';

export interface BanquetteLaborEstimate {
  labor_details: Array<{
    rate_name: string;
    rate_id: string;
    category: string;
    unit: string;
    units: number;
    rate_per_unit: number;
    cost: number;
    score_breakdown?: Array<{ label: string; minutes: number; cost: number }>;
  }>;
  total_labor_cost: number;
}

export function useLaborRates() {
  return useQuery({
    queryKey: ['labor-rates'],
    queryFn: async () => {
      const { data } = await apiClient.get<LaborRate[]>('/labor-rates');
      return data;
    },
  });
}

export function useLaborRate(id: string | null) {
  return useQuery({
    queryKey: ['labor-rates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<LaborRate>(`/labor-rates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateLaborRate) => {
      const { data } = await apiClient.post<LaborRate>('/labor-rates', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labor-rates'] }),
  });
}

export function useUpdateLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateLaborRate & { id: string }) => {
      const { data } = await apiClient.patch<LaborRate>(`/labor-rates/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['labor-rates'] });
      queryClient.invalidateQueries({ queryKey: ['labor-rates', vars.id] });
    },
  });
}

export function useDeleteLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/labor-rates/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labor-rates'] }),
  });
}

export function useBulkDeleteLaborRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await apiClient.post('/labor-rates/bulk-delete', { ids });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labor-rates'] }),
  });
}

export function useEstimateBanquetteLabor() {
  return useMutation({
    mutationFn: async (banquetteConfig: any) => {
      const { data } = await apiClient.post<BanquetteLaborEstimate>(
        '/labor-rates/estimate-banquette',
        { banquette_config: banquetteConfig },
      );
      return data;
    },
  });
}

export interface BatchLaborEstimate {
  id: string;
  total_labor_cost: number;
  labor_details: BanquetteLaborEstimate['labor_details'];
}

/** One request for every template on the Cria Library page. */
export function useEstimateBanquetteLaborBatch() {
  return useMutation({
    mutationFn: async (items: Array<{ id: string; config: any }>) => {
      const { data } = await apiClient.post<BatchLaborEstimate[]>(
        '/labor-rates/estimate-banquette-batch',
        { items },
      );
      return data;
    },
  });
}
